import React from 'react';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';
import { useConfirm } from 'material-ui-confirm';

import { FormControl, RadioGroup, Typography } from '@mui/material';

import { Wallbox } from '../atoms/icons/wallbox';
import { ChartBox } from '../molecules/chart-box';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { RadioBox } from '../molecules/radio-box';
import { Group } from '../organisms/group';
import { FormattedValue } from '../store/formatted-value';
import { useDispatch, useSelector } from '../store/utils';
import { SliderBox } from '../molecules/slider-box';
import { ParentPaper } from '../atoms/parent-paper';
import { FormattedWallboxStatus } from '../atoms/formatted-wallbox-status';
import { NavigationList, NavigationItem } from '../molecules/navigation-list';
import { Rfid } from '../atoms/icons/rfid';
import { links } from '../paths';
import { variablesChange } from '../store/actions';
import { Schedules } from '../organisms/schedules/schedules';
import { evScheduleTypes } from '../organisms/schedules/schedules.types';

const WallboxPage = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const solaxWallbox = useSelector((s) => get(s.variables, 'WB_TYPE') === 1);
  const multipleWallboxes = useSelector(
    (s) =>
      get(s.variables, 'WB_TYPE') === 'next' &&
      Object.keys(get(s.variables, 'WALLBOX_CONFIG') || {}).length > 1
  );
  const wallboxConfig = useSelector(
    (s) => get(s.variables, 'WALLBOX_CONFIG') || {}
  );
  const wallboxKeys = useSelector((s) => {
    const config = get(s.variables, 'WALLBOX_CONFIG') || {};
    return Object.keys(config);
  });
  const rfidVisible = useSelector((s) =>
    Boolean(get(s.variables, 'RFID_VISIBLE'))
  );
  const premium = useSelector((s) => Boolean(get(s.variables, 'PREMIUM')));

  const mode = useSelector((s) => {
    if (s.variables.WB_MODE_NONE) {
      return 'none';
    } else if (s.variables.WB_MODE_SPOT) {
      return 'spot';
    } else if (s.variables.WB_MODE_PV) {
      return 'pv';
    } else if (s.variables.WB_MODE_PV_FAST) {
      return 'pvFast';
    } else if (s.variables.WB_MODE_PV2) {
      return 'pv2';
    } else if (s.variables.WB_MODE_NO_CHARGE) {
      return 'noCharge';
    }
  });

  const batteryStopWb = useSelector((s) =>
    Boolean(get(s.variables, 'PV_BATTERY_STOP_WB'))
  );

  const onModeChange = (
    newMode: 'none' | 'spot' | 'pv' | 'pv2' | 'pvFast' | 'noCharge'
  ) => {
    if ((newMode === 'pv' || newMode === 'pvFast') && batteryStopWb) {
      confirm({
        title: (
          <FormattedMessage
            id="wallbox.mode.pv.batteryStopConflict.title"
            defaultMessage="Režim nelze zapnout"
          />
        ),
        description: (
          <FormattedMessage
            id="wallbox.mode.pv.batteryStopConflict.description"
            defaultMessage="Pokud je zapnutá blokace baterie při provozu wallboxu, nelze zapnout režim „Nabíjení podle baterie“. Pro zapnutí režimu „Nabíjení podle baterie“ je nutné nejprve vypnout blokaci baterie na stránce „Fotovoltaika“."
          />
        ),
        confirmationText: (
          <FormattedMessage id="close" defaultMessage="Zavřít" />
        ),
        hideCancelButton: true,
      }).catch(() => {
        // noop
      });
      return;
    }

    dispatch(
      variablesChange({
        WB_MODE_NONE: newMode === 'none',
        WB_MODE_SPOT: newMode === 'spot',
        WB_MODE_PV: newMode === 'pv',
        WB_MODE_PV2: newMode === 'pv2',
        WB_MODE_PV_FAST: newMode === 'pvFast',
        WB_MODE_NO_CHARGE: newMode === 'noCharge',
      })
    );
  };

  return (
    <>
      <Group layout="list">
        <SwitchBox
          title={
            <FormattedMessage
              id="wallbox.status"
              defaultMessage="Režim Wallboxu"
            />
          }
          subtitle={<FormattedWallboxStatus />}
          switchVariableId="WB_ON"
          fullWidth
        />
      </Group>

      {/* chart */}
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="wallbox.statistics"
            defaultMessage="Statistiky"
          />
        }
      >
        <ChartBox
          variableId="WB_CONSUMED_GRAPH"
          getSubStatsLabel={(key) =>
            (get(wallboxConfig, [key, 'name']) as string) || key
          }
        />
      </Group>

      {/* general information */}
      <Group
        title={
          <FormattedMessage
            id="wallbox.common"
            defaultMessage="Obecné informace"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="wallbox.common.power"
              defaultMessage="Aktuální spotřeba"
            />
          }
          value={
            <FormattedValue
              variableId="WB_ACTUAL_POWER"
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Wallbox sx={{ width: 20 }} />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="wallbox.common.power_total"
              defaultMessage="Celková spotřeba"
            />
          }
          value={
            <FormattedValue
              variableId={
                !solaxWallbox
                  ? 'NEW_WB_ENERGY_CONSUMPTION_TOTAL'
                  : 'STATS_WB_ENERGY_CONSUMPTION_TOTAL'
              }
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<Wallbox sx={{ width: 20 }} />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="wallbox.fast-charge.energy_actual"
                defaultMessage="Aktuální cyklus:"
              />
              &nbsp;
              <FormattedValue
                variableId="WB_ENERGY_CONSUMPTION_NOW"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="wallbox.common.power_today"
                defaultMessage="Dnešní spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_WB_ENERGY_CONSUMPTION_TODAY"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="wallbox.common.power_year"
                defaultMessage="Roční spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_WB_ENERGY_CONSUMPTION_YEAR"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            </Typography>
          }
        />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="wallbox.control"
            defaultMessage="Režim nabíjení"
          />
        }
      >
        <ParentPaper>
          <FormControl sx={{ width: '100%' }}>
            <RadioGroup
              aria-labelledby="wallbox-mode-label"
              name="wallbox-mode"
              value={mode}
              onChange={(_e, v) =>
                onModeChange(
                  v as 'none' | 'spot' | 'pv' | 'pv2' | 'pvFast' | 'noCharge'
                )
              }
            >
              <RadioBox
                title={
                  <FormattedMessage
                    id="wallbox.nonemode"
                    defaultMessage="Běžné nabíjení"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="wallbox.nonemode.on_sub"
                    defaultMessage="Auto se nabíjí co nejrychleji"
                  />
                }
                fullWidth
                value="none"
              />

              <RadioBox
                title={
                  <FormattedMessage
                    id="wallbox.SPOT"
                    defaultMessage="Nabíjení podle spotu"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="wallbox.SPOT.on_sub"
                    defaultMessage="Po dosažení základní energie dojde k blokování při vysoké ceně spotu"
                  />
                }
                visibility={{ variableId: 'SP_INSTALL' }}
                fullWidth
                value="spot"
              />

              <RadioBox
                title={
                  <FormattedMessage
                    id="wallbox.PV"
                    defaultMessage="Nabíjení podle baterie"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="wallbox.PV.on_sub"
                    defaultMessage="Po dosažení základní energie dojde k nabíjení pouze při nabité baterii"
                  />
                }
                fullWidth
                visibility={{ variableIds: ['PV_INSTALL', 'PV_BATTERY'] }}
                value="pv"
              />

              <RadioBox
                title={
                  <FormattedMessage
                    id="wallbox.PV_FAST"
                    defaultMessage="Přednostní nabíjení podle baterie"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="wallbox.PV_FAST.on_sub"
                    defaultMessage="Nabíjení automobilu bude zcela prioritní. Bude využívána pouze energie z baterie nebo FVE"
                  />
                }
                fullWidth
                visibility={{ variableIds: ['PV_INSTALL', 'PV_BATTERY'] }}
                value="pvFast"
              />

              {solaxWallbox && (
                <RadioBox
                  title={
                    <FormattedMessage
                      id="wallbox.PV2"
                      defaultMessage="Nabíjení podle FVE"
                    />
                  }
                  subtitle={
                    <FormattedMessage
                      id="wallbox.PV2.on_sub"
                      defaultMessage="Řízení nabíjení podle výkonu FVE"
                    />
                  }
                  fullWidth
                  value="pv2"
                />
              )}
              {premium && (
                <RadioBox
                  title={
                    <FormattedMessage
                      id="wallbox.NO_CHARGE"
                      defaultMessage="Nenabíjej"
                    />
                  }
                  subtitle={
                    <FormattedMessage
                      id="wallbox.NO_CHARGE.on_sub"
                      defaultMessage="Nabíjení je trvale blokováno, spustí jej pouze časový plán"
                    />
                  }
                  fullWidth
                  value="noCharge"
                />
              )}
            </RadioGroup>
          </FormControl>
        </ParentPaper>
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage id="wallbox.settings" defaultMessage="Nastavení" />
        }
      >
        <ParentPaper>
          {mode === 'none' && (
            <>
              <SliderBox
                title={
                  <FormattedMessage
                    id="wallbox.nonemode.ampers"
                    defaultMessage="Maximální nabíjecí proud"
                  />
                }
                visibility={{ variableId: 'WB_MODE_NONE' }}
                variableId="WB_MAX_CURRENT_NONE_MODE"
                opts={{
                  style: 'unit',
                  unit: 'ampere',
                  maximumFractionDigits: 0,
                }}
                min={6}
                max={32}
              />
              <SwitchBox
                title={
                  <FormattedMessage
                    id="wallbox.nonemode.hdo"
                    defaultMessage="Nabíjení pouze při nízkém tarifu"
                  />
                }
                switchVariableId="WB_MODE_NONE_HDO"
                visibility={{ variableId: 'WB_MODE_NONE' }}
                subtitle={
                  <FormattedMessage
                    id="wallbox.nonemode.hdo_sub"
                    defaultMessage="Časy pro HDO musí být nastaveny v obecném nastavení"
                  />
                }
                fullWidth
              />
            </>
          )}

          {mode === 'spot' && (
            <>
              <SliderBox
                title={
                  <FormattedMessage
                    id="wallbox.nonemode.ampers"
                    defaultMessage="Maximální nabíjecí proud"
                  />
                }
                visibility={{ variableId: 'WB_MODE_SPOT' }}
                variableId="WB_MAX_CURRENT_SPOT"
                min={6}
                max={32}
                opts={{
                  style: 'unit',
                  unit: 'ampere',
                  maximumFractionDigits: 0,
                }}
              />
              <SwitchBox
                title={
                  <FormattedMessage
                    id="wallbox.SPOT.mi"
                    defaultMessage="Blokovat nabíjení při střední ceně"
                  />
                }
                switchVariableId="WB_SPOT_MI_STOP"
                visibility={{ variableId: 'WB_MODE_SPOT' }}
                fullWidth
              />
              <SwitchBox
                title={
                  <FormattedMessage
                    id="wallbox.SPOT.hi"
                    defaultMessage="Blokovat nabíjení při vysoké ceně"
                  />
                }
                switchVariableId="WB_SPOT_HI_STOP"
                visibility={{ variableId: 'WB_MODE_SPOT' }}
                fullWidth
              />
            </>
          )}

          {mode === 'pv' && (
            <>
              <SwitchBox
                title={
                  <FormattedMessage
                    id="wallbox.nonemode.amper-auto"
                    defaultMessage="Automatické řízení nabíjecího proudu"
                  />
                }
                switchVariableId="WB_MAX_CURRENT_BATTERY_AUTO"
                subtitle={
                  <FormattedMessage
                    id="wallbox.nonemode.amper-auto.subtitle"
                    defaultMessage="Řídí nabíjení aby se minimalizovalo nabíjení a vybíjení baterie"
                  />
                }
                fullWidth
                visibility={{ variableId: 'WB_MODE_PV' }}
              />
              <ValueBox
                type="row"
                title={
                  <>
                    <FormattedMessage
                      id="wallbox.pv.ampers-auto"
                      defaultMessage="Aktuální požadovaný nabíjecí proud"
                    />
                  </>
                }
                value={
                  <FormattedValue
                    variableId="WB_MAX_CURRENT"
                    opts={{
                      style: 'unit',
                      unit: 'ampere',
                      maximumFractionDigits: 0,
                    }}
                  />
                }
                visibility={{
                  variableIds: [
                    'WB_MAX_CURRENT_BATTERY_AUTO',
                    'PV_BATTERY',
                    'WB_MODE_PV',
                  ],
                }}
              />
              <SliderBox
                title={
                  <FormattedMessage
                    id="wallbox.nonemode.ampers"
                    defaultMessage="Maximální nabíjecí proud"
                  />
                }
                visibility={{
                  variableIds: ['PV_BATTERY', 'WB_MODE_PV'],
                }}
                variableId="WB_MAX_CURRENT_BATTERY"
                opts={{
                  style: 'unit',
                  unit: 'ampere',
                  maximumFractionDigits: 0,
                }}
                min={6}
                max={32}
              />
              <SliderBox
                title={
                  <FormattedMessage
                    id="wallbox.PV.batteryStart"
                    defaultMessage="Zapnout při nabití"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="wallbox.PV.batteryStart.subtitle"
                    defaultMessage="Nabití baterie při kterém se aktivuje nabíjení"
                  />
                }
                visibility={{ variableId: 'WB_MODE_PV' }}
                variableId={'WB_BAT_START'}
                opts={{ style: 'percent', maximumFractionDigits: 0 }}
                min={20}
              />
              <SliderBox
                title={
                  <FormattedMessage
                    id="wallbox.PV.batteryStop"
                    defaultMessage="Vypnout při nabití"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="wallbox.PV.batteryStop.subtitle"
                    defaultMessage="Nabití baterie při kterém se vypne nabíjení"
                  />
                }
                visibility={{ variableId: 'WB_MODE_PV' }}
                variableId={'WB_BAT_STOP'}
                opts={{ style: 'percent', maximumFractionDigits: 0 }}
                min={20}
              />
            </>
          )}

          {mode === 'pvFast' && (
            <>
              <ValueBox
                type="row"
                title={
                  <FormattedMessage
                    id="wallbox.pv.ampers-auto"
                    defaultMessage="Aktuální požadovaný nabíjecí proud"
                  />
                }
                value={
                  <FormattedValue
                    variableId="WB_MAX_CURRENT"
                    opts={{
                      style: 'unit',
                      unit: 'ampere',
                      maximumFractionDigits: 0,
                    }}
                  />
                }
                visibility={{
                  variableIds: ['PV_BATTERY', 'WB_MODE_PV_FAST'],
                }}
              />
              <SliderBox
                title={
                  <FormattedMessage
                    id="wallbox.nonemode.ampers"
                    defaultMessage="Maximální nabíjecí proud"
                  />
                }
                visibility={{
                  variableIds: ['PV_BATTERY', 'WB_MODE_PV_FAST'],
                }}
                variableId="WB_MAX_CURRENT_BATTERY_FAST"
                opts={{
                  style: 'unit',
                  unit: 'ampere',
                  maximumFractionDigits: 0,
                }}
                min={6}
                max={32}
              />
              <SliderBox
                title={
                  <FormattedMessage
                    id="wallbox.PV_FAST.batteryMinSoc"
                    defaultMessage="Minimální rezerva baterie"
                  />
                }
                subtitle={
                  <FormattedMessage
                    id="wallbox.PV_FAST.batteryMinSoc.subtitle"
                    defaultMessage="Baterie se kvůli nabíjení automobilu nevybije pod tuto hodnotu. Dokud není hodnota překročena, nabíjení čeká"
                  />
                }
                visibility={{
                  variableIds: ['PV_BATTERY', 'WB_MODE_PV_FAST'],
                }}
                variableId="WB_BAT_FAST_MIN_SOC"
                opts={{ style: 'percent', maximumFractionDigits: 0 }}
                min={10}
                max={90}
              />
            </>
          )}

          {mode === 'pv2' && (
            <>
              <SwitchBox
                title={
                  <FormattedMessage
                    id="wallbox.3a.on"
                    defaultMessage="Mód 3A"
                  />
                }
                switchVariableId="WB_MODE_PV2_3A"
                visibility={{ variableId: 'WB_MODE_PV2' }}
                fullWidth
                subtitle={
                  <FormattedMessage
                    id="wallbox.3a.on_sub"
                    defaultMessage="Nabíjení může brát 3A z distribuční sítě"
                  />
                }
              />
              <SwitchBox
                title={
                  <FormattedMessage
                    id="wallbox.6a.on"
                    defaultMessage="Mód 6A"
                  />
                }
                switchVariableId="WB_MODE_PV2_6A"
                visibility={{ variableId: 'WB_MODE_PV2' }}
                fullWidth
                subtitle={
                  <FormattedMessage
                    id="wallbox.6a.on_sub"
                    defaultMessage="Nabíjení lze pouze z FVE"
                  />
                }
              />
            </>
          )}

          {mode !== 'none' && mode !== 'pvFast' && mode !== 'noCharge' && (
            <SliderBox
              title={
                <FormattedMessage
                  id="wallbox.fast-charge.energy"
                  defaultMessage="Energie pro rychlé nabití"
                />
              }
              subtitle={
                <FormattedMessage
                  id="wallbox.fast-charge.energy_sub"
                  defaultMessage="Energie, která je vždy co nejrychleji nabita. Po dosažení této hodnoty je nabití řízeno jedním z režimů"
                />
              }
              variableId="WB_MIN_CHARGE_ENERGY"
              min={0}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 0,
              }}
            />
          )}
        </ParentPaper>
      </Group>

      {/* other */}
      {(rfidVisible || multipleWallboxes) && (
        <Group
          title={
            <FormattedMessage id="wallbox.other" defaultMessage="Ostatní" />
          }
          layout="list"
        >
          <NavigationList>
            {multipleWallboxes && (
              <NavigationItem
                icon={<Wallbox />}
                label={
                  <FormattedMessage
                    id="wallbox.wallboxes.title"
                    defaultMessage="Wallboxy"
                  />
                }
                to={links.wallboxList}
              />
            )}
            {rfidVisible && (
              <NavigationItem
                icon={<Rfid />}
                label={
                  <FormattedMessage
                    id="rfid_cards.title"
                    defaultMessage="RFID karty"
                  />
                }
                to={links.rfidCards}
              />
            )}
          </NavigationList>
        </Group>
      )}

      {premium && (
        <Schedules
          types={evScheduleTypes}
          title={
            <FormattedMessage
              id="wallbox.schedules.title"
              defaultMessage="Plánovač nabíjení"
            />
          }
          description={
            <FormattedMessage
              id="wallbox.schedules.description"
              defaultMessage="Naplánujte si nabíjení elektromobilu podle potřeby."
            />
          }
        />
      )}
    </>
  );
};

export default WallboxPage;
